import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ExtendedUserDto, RemoteConfigService, UserRemoteControllerService } from 'core/api';
import { IUserDto } from 'core/dto';
import {LoaderService, MessageBoxService, SnackbarService} from 'core/services';
import {BehaviorSubject, filter, map, switchMap, take} from 'rxjs';
import { designerPaths } from '../../designer.paths';
import { UserService } from '../../global-services/user.service';
import { DsgDeepTranslationKey } from '../../pipes/translate.pipe';
import { IColumn } from '../../shared-components/data-grid/data-grid.component';

export const NEW_USER = 'new-user'

@Injectable()
export class UserManagementService {

  readonly usersImported = new BehaviorSubject<boolean>(false);

  get userRoles() {
    return this._userService.userSubject.value?.roleIds;
  }

  readonly rolesSubject = this._remoteConfigService.rolesSubject;
  get roles() {
    return this.rolesSubject.value;
  }

  readonly columns: IColumn<IUserDto>[] = [
    {
      caption: 'ss-entities.user.status.display',
      dataField: 'status',
      type: 'string',
      showInCollapsed: true,

    },
    { caption: 'ss-entities.user.first-name', dataField: 'firstName', type: 'string' },
    { caption: 'ss-entities.user.last-name', dataField: 'lastName', type: 'string' },
    {
      caption: null,
      dataField: 'full-name',
      type: 'string',
      calculateDisplay: (element) => element.firstName + ' ' + element.lastName,
      title: true
    },
    {
      caption: 'ss-entities.user.email',
      dataField: 'email',
      type: 'string',
      icon: 'email',
      showInCollapsed: true
    },
    {
      caption: 'ss-entities.user.mobile',
      dataField: 'mobile',
      type: 'string',
      showInCollapsed: true,
      icon: 'phone'
    },
    {
      caption: 'ss-entities.user.role', dataField: 'roleIds', type: 'string',
      calculateDisplay: (data) => this.rolesSubject.pipe(
        filter(Boolean),
        map(roles => roles.filter((role) => data.roleIds.includes(role.id)).map(({ description }) => description).join(', '))
      )
    },

  ]


  readonly load = (skip: number, take: number, search: string) => {
    search = search ? '%' + search + '%' : undefined

    return this._userRemoteControllerService.getUsersByCriteria({
      page: skip / take,
      size: take,
      email: search,
      firstName: search,
      lastName: search,
      phone: search,
      sort: ['firstName'],

    })
  }

  readonly customizeRow = (element: HTMLElement, row: ExtendedUserDto) => {
    element.classList.toggle('warn-background-100-transparent', row.delete)
    element.classList.toggle('background-lightgray-transparent', !row.active)
  }


  constructor(
    private readonly _userRemoteControllerService: UserRemoteControllerService,
    private readonly _remoteConfigService: RemoteConfigService,
    private readonly _router: Router,
    private readonly _snackbarService: SnackbarService<DsgDeepTranslationKey>,
    private readonly _messageBoxService: MessageBoxService<DsgDeepTranslationKey>,
    private readonly _translateService: TranslateService,
    private readonly _userService: UserService,
    private readonly _loaderService: LoaderService
  ) {

  }

  routeToNewUser() {
    this._router.navigate([designerPaths.userManagement, NEW_USER])
  }

  routeToUser(user: ExtendedUserDto) {
    this._router.navigate([designerPaths.userManagement, user.id])
  }

  deleteUser(userDto: ExtendedUserDto) {

    const text = [this._translateService.instant('ss-entities.user.remove.message' as DsgDeepTranslationKey), '"' + userDto.firstName, userDto.lastName + '"', '?'].join(' ');

    return this._messageBoxService.show({
      type: 'prompt',
      buttons: 'YesNo',
      title: 'ss-entities.user.remove.title',
      text: text as DsgDeepTranslationKey
    })
      .pipe(
        take(1),
        filter((response) => response === 'Yes'),
        switchMap(() => this._userRemoteControllerService.remove(userDto.id)
          .pipe(
            take(1),
            this._snackbarService.handleAsync({
              errorMessage: 'ss-entities.user.remove.error',
              successMessage: 'ss-entities.user.remove.success',
            })
          ))
      )


  }

  importUsers() {
    const id = this._loaderService.show({ key: 'user.import' , text: 'ss-entities.user.import.pending' })

    return this._userRemoteControllerService.importFromAd()
      .pipe(
        take(1),
        this._loaderService.disposeLoader(id),
        this._snackbarService.handleAsync({
          errorMessage: 'ss-entities.user.import.error',
          successMessage: 'ss-entities.user.import.success',
        })
      )
  }
}
